import { Fragment, useState, useEffect, useRef } from "react"
import CHAMPS from "./data/champion/manual_champs.json"
import _ELEMENTS from "./data/common/elements.json"
import CheckIcon from "@mui/icons-material/Check"
import ClearIcon from "@mui/icons-material/Backspace"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import { useAppSizeContext, useElementSize } from "./context/AppSizeContext"
import { useIgnoredContext } from "./context/IgnoredContext"
import { CARD_RATIO } from "./constants"

let ELEMENTS = _ELEMENTS
  .filter((str) => !["NORM", "FIRE", "WATER", "WIND", "EXALTED"].includes(str))
  .sort()
ELEMENTS = ["NORM", "FIRE", "WATER", "WIND", ...ELEMENTS]

const champMap = {
  Spirit: 0,
  Lv1: 1,
  Lv2: 2,
  Lv3: 3,
}

const LEVELS = Object.keys(champMap)

const MAX_PER_LEVEL = 3

// shown when a 4th pick of the same level is refused
const MAX_MESSAGES = {
  Spirit: "Three spirits is a séance. Four is a haunting. Stopping you here.",
  Lv1: "Three Lv 1s covers every opening you'll ever see. The fourth stays home.",
  Lv2: "You get three Lv 2s. Nobody has ever needed a fourth. Nobody.",
  Lv3: "Three Lv 3s is already a power fantasy. Four is just showing off.",
}

// display names and well glyphs for the lineup; the champMap keys
// stay the data keys ("Lv1") everywhere else
const LEVEL_META = {
  Spirit: { label: "Spirit", glyph: "✦" },
  Lv1: { label: "Lv 1", glyph: "I" },
  Lv2: { label: "Lv 2", glyph: "II" },
  Lv3: { label: "Lv 3", glyph: "III" },
}

function getIconLink(element) {
  return `https://cdn2.gatcg.com/i/elements/${element.toLowerCase()}.png`
  // return `https://index.gatcg.com/images/icons/elements/${}.png`
}

const getDimensions = (height) => ({
  width: height * CARD_RATIO,
  height,
})

const fallbackLineup = [CHAMPS[0].find((card) => card.name === "Lost Spirit")]

export const Pile = ({
  type,
  cards,
  height,
  onEnter,
  onLeave,
  radius,
  mult,
  noBorder,
}) => {
  const [hovIdx, setHovIdx] = useState(null)
  return (
    <div
      className="manual-pile-container"
      style={{
        position: "relative",
        ...getDimensions(height),
      }}
    >
      {cards.length > 0 ? (
        cards.map((card, idx) => {
          const url = card.computed.imgUrl
          return (
            <img
              key={url}
              onMouseEnter={() => {
                setHovIdx(idx)
                onEnter?.(card)
              }}
              onMouseLeave={() => {
                setHovIdx(null)
                onLeave?.(card)
              }}
              alt={url.split("/").pop().trim()}
              src={url}
              style={{
                borderRadius: radius ?? 12,
                ...getDimensions(height),
                boxShadow: "var(--card-shadow)",
                marginTop: idx * (mult ?? 34),
                position: "absolute",
                // 1 beats the sibling cards (auto) across all piles but
                // stays below overlays: drawer backdrop (5), drawer (10)
                zIndex: idx === hovIdx ? 1 : undefined,
                left: 0,
                top: 0,
                border: noBorder ? 0 : "1px solid var(--accent)",
              }}
            />
          )
        })
      ) : (
        <div className="pile-placeholder" style={getDimensions(height)}>
          {type}
        </div>
      )}
    </div>
  )
}

export const ManualChampPicker = ({ onSelect, onExplore }) => {
  const { isSmall } = useAppSizeContext()
  const { ignoredSet } = useIgnoredContext()

  const [levelName, setLevelName] = useState("Spirit")
  const [hoveredChamp, setHoveredChamp] = useState(null)

  // touch screens have no hover — tapping a row/thumb opens a modal
  // preview with explicit Add/Remove instead of acting immediately
  const [tapPreview, setTapPreview] = useState(null) // { card, level }

  // if the window grows past the breakpoint while the tap preview is
  // open, drop it — desktop renders no modal, and a stale card would
  // otherwise reappear on the next shrink
  useEffect(() => {
    if (!isSmall) setTapPreview(null)
  }, [isSmall])

  useEffect(() => {
    if (!tapPreview) return
    const onKey = (evt) => {
      if (evt.key === "Escape") setTapPreview(null)
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [tapPreview])

  // on touch screens every tap previews first; true = tap consumed
  function maybeTapPreview(card, level) {
    if (!isSmall) return false
    setTapPreview({ card, level })
    return true
  }

  // stamped on every add so the second click of a double-click can be
  // swallowed: after an auto-advance the list swapped under the cursor
  // (any row must be ignored), and without one the same row is now
  // isPicked and would silently toggle the pick back off
  const lastAddRef = useRef(null) // { t, card, advanced }

  // The preview box gets its height from flex layout; measure it and
  // set the width explicitly — an aspect-ratio-derived width doesn't
  // propagate into the parent flex column's sizing. Small height
  // changes are ignored so a scrollbar appearing/disappearing can't
  // oscillate the layout.
  const { elementRef: previewRef, size: previewSize } = useElementSize()
  const [previewWidth, setPreviewWidth] = useState(null)
  useEffect(() => {
    const h = previewSize.height
    if (!h) return
    const w = Math.round(getDimensions(h).width)
    setPreviewWidth((prev) =>
      prev !== null && Math.abs(w - prev) <= 16 ? prev : w,
    )
  }, [previewSize.height])
  const previewStyle = previewWidth ? { width: previewWidth } : undefined

  const [search, setSearch] = useState("")
  const [filter, setFilter] = useState([])

  const [champs, setChamps] = useState({
    Spirit: [],
    Lv1: [],
    Lv2: [],
    Lv3: [],
  })

  useEffect(() => {
    const list = Object.values(champs).flat()
    onSelect(list.length ? list : fallbackLineup)
  }, [onSelect, champs])

  // the search string persists across level switches — only the
  // clear button (or the user editing it) changes it
  function handleSelectLevel(type) {
    if (type === levelName) return
    setLevelName(type)
  }

  function handleAddChamp(card, level = levelName) {
    // no deck realistically runs more than 4 of one level
    if (champs[level].length >= MAX_PER_LEVEL) {
      window.alert(MAX_MESSAGES[level])
      return
    }
    const wasEmpty = champs[level].length === 0
    setChamps((map) => {
      map[level].push(card)
      return { ...map }
    })
    lastAddRef.current = { t: Date.now(), card, advanced: false }
    // advance to the next empty level so the flow reads
    // Spirit → Lv1 → Lv2 → Lv3 without extra clicks — but only on a
    // level's first pick; coming back to a filled level means the
    // player wants several of that level, so stay put
    if (!wasEmpty) return
    const idx = LEVELS.indexOf(level)
    const next = LEVELS.slice(idx + 1).find((lv) => champs[lv].length === 0)
    if (next) {
      lastAddRef.current.advanced = true
      handleSelectLevel(next)
    }
  }

  function handleRemoveChamp(card, level = levelName) {
    setChamps((map) => {
      map[level] = map[level].filter((c) => c !== card)
      return { ...map }
    })
  }

  function handleAddFilter(el) {
    setFilter((list) => {
      return [...list, el]
    })
  }

  function handleRemoveFilter(el) {
    setFilter((list) => {
      return list.filter((x) => x !== el)
    })
  }

  const flatChamps = Object.values(champs).flat()

  const champList = !levelName
    ? []
    : CHAMPS[champMap[levelName]]
        // same "always hidden" filter the card search applies
        .filter((card) => !ignoredSet.has(card.name))
        .filter((card) => {
          // by element filter
          if (filter.length) {
            return filter.includes(card.element)
          }
          return true
        })
        .filter((card) => {
          // by search string
          if (search) {
            return (
              card.computed.name.toLowerCase().includes(search.toLowerCase()) ||
              card.computed.rawEffect
                ?.toLowerCase()
                .includes(search.toLowerCase())
            )
          }
          return true
        })
        .sort((a, b) => {
          return a.name.localeCompare(b.name)
        })
        .map((card, idx) => {
          const isPicked = flatChamps.includes(card)

          return (
            <div
              key={idx}
              className={`champ-opt${isPicked ? " picked" : ""}`}
              onMouseEnter={() => setHoveredChamp(card.computed.imgUrl)}
              onMouseLeave={() => setHoveredChamp(null)}
              onClick={() => {
                // swallow the second click of a double-click: after an
                // auto-advance this row is an unrelated champion; with
                // no advance it's the row just added, now isPicked, and
                // the click would silently remove it again
                const guard = lastAddRef.current
                if (
                  guard &&
                  Date.now() - guard.t < 350 &&
                  (guard.advanced || guard.card === card)
                )
                  return
                if (maybeTapPreview(card, levelName)) return
                if (isPicked) {
                  handleRemoveChamp(card)
                } else {
                  handleAddChamp(card)
                }
              }}
            >
              {card.name}
              {isPicked && (
                <CheckIcon
                  fontSize="small"
                  style={{ position: "absolute", right: 10 }}
                />
              )}
            </div>
          )
        })

  return (
    <>
      <div className="picker">
        {isSmall && (
          <div
            className={`spirit-bar${levelName === "Spirit" ? " active" : ""}`}
            onClick={() => handleSelectLevel("Spirit")}
          >
            {champs.Spirit.length === 0 ? (
              <button
                type="button"
                className="spirit-bar-label"
                title="Pick Spirit champions"
                onClick={(evt) => {
                  evt.stopPropagation()
                  handleSelectLevel("Spirit")
                }}
              >
                <span className="spirit-bar-glyph" aria-hidden="true">
                  {LEVEL_META.Spirit.glyph}
                </span>
                <span className="slot-name">Spirit</span>
                {levelName === "Spirit" && (
                  <span className="slot-hint">pick below</span>
                )}
              </button>
            ) : (
              champs.Spirit.map((card) => (
                <button
                  type="button"
                  key={card.computed.imgUrl}
                  className="spirit-strip"
                  title={`Preview ${card.name}`}
                  onClick={(evt) => {
                    evt.stopPropagation()
                    maybeTapPreview(card, "Spirit")
                  }}
                >
                  <img alt={card.name} src={card.computed.imgUrl} />
                </button>
              ))
            )}
          </div>
        )}

        <div className="lineup-row">
          {(isSmall ? LEVELS.slice(1) : LEVELS).map((type, idx, levels) => {
            const list = champs[type]
            const active = levelName === type
            const filled = list.length > 0
            return (
              <Fragment key={type}>
                {idx > 0 && (
                  <span
                    className={`lineup-arrow${
                      champs[levels[idx - 1]].length > 0 ? " lit" : ""
                    }`}
                    aria-hidden="true"
                  >
                    ›
                  </span>
                )}
                <div
                  className={`slot${active ? " active" : ""}`}
                  onClick={() => handleSelectLevel(type)}
                >
                  <button
                    type="button"
                    className="slot-well"
                    title={`Pick ${LEVEL_META[type].label} champions`}
                    onClick={(evt) => {
                      evt.stopPropagation()
                      handleSelectLevel(type)
                    }}
                  >
                    <span className="slot-corners" aria-hidden="true" />
                    {!filled && (
                      <span className="slot-glyph" aria-hidden="true">
                        {LEVEL_META[type].glyph}
                      </span>
                    )}
                    {!filled && (
                      <span className="slot-name">
                        {LEVEL_META[type].label}
                      </span>
                    )}
                    {!filled && active && (
                      <span className="slot-hint">pick below</span>
                    )}
                  </button>
                  {filled && (
                    <div className="slot-cards">
                      {list.map((card) => (
                        <span
                          className="slot-card"
                          key={card.computed.imgUrl}
                          onMouseEnter={() =>
                            setHoveredChamp(card.computed.imgUrl)
                          }
                          onMouseLeave={() => setHoveredChamp(null)}
                        >
                          <button
                            type="button"
                            className="slot-card-face"
                            title={
                              isSmall
                                ? `Preview ${card.name}`
                                : `Add more ${LEVEL_META[type].label}s`
                            }
                            onClick={(evt) => {
                              evt.stopPropagation()
                              if (maybeTapPreview(card, type)) return
                              handleSelectLevel(type)
                            }}
                          >
                            <img alt={card.name} src={card.computed.imgUrl} />
                          </button>
                          {!isSmall && (
                            <button
                              type="button"
                              className="slot-card-remove"
                              title={`Remove ${card.name}`}
                              onClick={(evt) => {
                                evt.stopPropagation()
                                setHoveredChamp(null)
                                handleRemoveChamp(card, type)
                              }}
                            >
                              Remove
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </Fragment>
            )
          })}
        </div>

        <div className="picker-columns">
          <div className="picker-rail-col">
            <div className="picker-col-label">Element</div>
            <div className="element-rail">
              {ELEMENTS.map((str) => {
                const src = getIconLink(str)
                const active = filter.includes(str)

                return (
                  <div
                    key={str}
                    className={`element-opt${active ? " active" : ""}`}
                    onClick={() => {
                      if (active) {
                        handleRemoveFilter(str)
                      } else {
                        handleAddFilter(str)
                      }
                    }}
                  >
                    <img alt={str} src={src} />
                    {str}
                    {active && (
                      <ChevronLeftIcon
                        fontSize="small"
                        style={{ position: "absolute", right: 6 }}
                      />
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          <div className="picker-list-col">
            <div className="picker-col-label" style={{ marginBottom: -2 }}>
              {LEVEL_META[levelName].label}/Champions
            </div>
            <div className="picker-search">
              <input
                type="text"
                placeholder="Search card name or text…"
                value={search}
                onChange={(evt) => {
                  setSearch(evt.target.value)
                }}
              />
              <button
                type="button"
                title="Clear search"
                onClick={() => {
                  setSearch("")
                }}
              >
                <ClearIcon fontSize="small" />
              </button>
            </div>
            <div className="champ-list">
              {champList.length ? (
                champList
              ) : (
                <div className="champ-list-empty">
                  No champions match. Try clearing the search or element
                  filters.
                </div>
              )}
            </div>
          </div>

          <div className="picker-preview-col">
            <div className="picker-col-label">Preview</div>
            <div
              ref={previewRef}
              style={previewStyle}
              className={`picker-preview${hoveredChamp ? "" : " preview-placeholder"}`}
            >
              {hoveredChamp ? (
                <img alt="champion preview" src={hoveredChamp} />
              ) : (
                "Hover over a champion to preview it here"
              )}
            </div>
          </div>
        </div>
      </div>

      {isSmall && (
        <div className="mobile-cta-bar">
          <button className="btn-primary" onClick={onExplore}>
            Explore synergies
          </button>
        </div>
      )}

      {isSmall && tapPreview && (
        <div
          className="card-preview-backdrop"
          onClick={() => setTapPreview(null)}
        >
          <img
            alt={tapPreview.card.name}
            src={tapPreview.card.computed.imgUrl}
            onClick={(evt) => evt.stopPropagation()}
          />
          <div
            className={`card-preview-actions${
              flatChamps.includes(tapPreview.card) ? " three" : ""
            }`}
            onClick={(evt) => evt.stopPropagation()}
          >
            {flatChamps.includes(tapPreview.card) ? (
              <>
                <button
                  className="btn-plain danger"
                  onClick={() => {
                    handleRemoveChamp(tapPreview.card, tapPreview.level)
                    setTapPreview(null)
                  }}
                >
                  Remove from {LEVEL_META[tapPreview.level].label}
                </button>
                <button
                  className="btn-primary"
                  onClick={() => {
                    handleSelectLevel(tapPreview.level)
                    setTapPreview(null)
                  }}
                >
                  Pick more {LEVEL_META[tapPreview.level].label}s
                </button>
              </>
            ) : (
              <button
                className="btn-primary"
                onClick={() => {
                  handleAddChamp(tapPreview.card, tapPreview.level)
                  setTapPreview(null)
                }}
              >
                Add to {LEVEL_META[tapPreview.level].label}
              </button>
            )}
            <button className="btn-plain" onClick={() => setTapPreview(null)}>
              Close
            </button>
          </div>
        </div>
      )}
    </>
  )
}
